import { createFileRoute } from '@tanstack/react-router';
import { AlertTriangle, Check } from "lucide-react";
import { ContentHeader, ContentSection, FeatureShowcase, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/use-cases/data-synchronization')({
  component: DataSyncUseCasePage,
  head: () => ({
    meta: [
      { title: 'Data Synchronization | Matricks' },
      { name: 'description', content: 'Keep data consistent across all your systems. Automate real-time data flows between CRM, ERP, databases, and 2000+ applications.' },
    ],
  }),
});

const challenges = [
  "Data silos across departments and systems",
  "Manual data entry and copy-paste workflows",
  "Stale data causing poor decisions",
  "Inconsistent data across systems",
  "Complex ETL pipelines that are hard to maintain",
];

const solutions = [
  {
    iconName: "RefreshCw",
    title: "Real-Time Sync",
    description: "Changes propagate instantly across systems. No more waiting for batch jobs.",
  },
  {
    iconName: "Database",
    title: "2000+ Connectors",
    description: "Pre-built integrations for CRM, ERP, databases, and cloud services.",
  },
  {
    iconName: "Zap",
    title: "Event-Driven",
    description: "React to changes with webhooks and event triggers. Process data as it happens.",
  },
  {
    iconName: "Shield",
    title: "Data Quality",
    description: "Validation, transformation, and conflict resolution built in.",
  },
];

const syncPatterns = [
  {
    title: "One-Way Sync",
    description: "Push data from source to destination. Simple and reliable for reporting and analytics.",
  },
  {
    title: "Two-Way Sync",
    description: "Keep two systems in sync with bi-directional updates and conflict resolution.",
  },
  {
    title: "Event-Based",
    description: "Trigger sync only when specific events occur, reducing unnecessary processing.",
  },
  {
    title: "Scheduled",
    description: "Run sync jobs on schedules for batch processing and data warehousing.",
  },
];

const faqItems = [
  {
    question: "How do you handle data conflicts?",
    answer: "We support multiple conflict resolution strategies: last-write-wins, source priority, field-level merge, and custom rules. You define the strategy that makes sense for your use case.",
  },
  {
    question: "Can you sync large datasets?",
    answer: "Yes, we handle enterprise-scale data volumes. Initial bulk sync is optimized for performance, and ongoing incremental sync processes only changed records.",
  },
  {
    question: "What happens if a sync fails?",
    answer: "Failed syncs are automatically retried with exponential backoff. Persistent failures are logged and trigger alerts. You can review and manually retry failed records.",
  },
];

function DataSyncUseCasePage() {
  return (
    <div>
      <ContentHeader
        title="Data Synchronization"
        description="Keep your data consistent and up-to-date across all systems. Matricks automates data flows so your teams always have the latest information."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Use Cases", href: "/use-cases" },
          { label: "Data Synchronization" },
        ]}
        badge="Use Case"
      />

      {/* Challenges */}
      <ContentSection
        id="challenges"
        title="The Challenge"
        description="Data silos and manual processes slow down your business."
      >
        <div className="space-y-4">
          {challenges.map((challenge, index) => (
            <div key={index} className="flex items-start gap-4 p-4 rounded-lg bg-red-500/5 border border-red-500/20">
              <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <span>{challenge}</span>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* Solution */}
      <ContentSection
        id="solution"
        title="The Matricks Solution"
        background="muted"
      >
        <FeatureShowcase features={solutions} columns={2} variant="cards" />
      </ContentSection>

      {/* Sync Patterns */}
      <ContentSection
        id="patterns"
        title="Synchronization Patterns"
        description="Choose the right pattern for your needs."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {syncPatterns.map((pattern) => (
            <div key={pattern.title} className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold">{pattern.title}</h3>
              <p className="text-sm text-muted-foreground mt-2">{pattern.description}</p>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* Example Use Cases */}
      <ContentSection
        id="examples"
        title="Example Scenarios"
        background="muted"
      >
        <div className="space-y-6">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">CRM to ERP Sync</h3>
            <p className="text-sm text-muted-foreground mt-2">
              When a deal closes in Salesforce, automatically create a customer record in NetSuite,
              generate an invoice, and update inventory allocation.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Multi-Channel Inventory</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Keep inventory levels synchronized across Shopify, Amazon, and your warehouse system.
              Prevent overselling and automate restocking alerts.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Customer 360</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Aggregate customer data from support tickets, purchase history, and marketing interactions
              into a unified customer profile for personalization.
            </p>
          </div>
        </div>
      </ContentSection>

      {/* Benefits */}
      <ContentSection id="benefits" title="Key Benefits">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            "Eliminate manual data entry",
            "Always-current data across systems",
            "Reduce errors and inconsistencies",
            "Faster business processes",
            "Better decision-making with unified data",
            "Lower integration maintenance costs",
          ].map((benefit) => (
            <div key={benefit} className="flex items-center gap-3">
              <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
              <span>{benefit}</span>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="Start Syncing Your Data"
        description="Connect your systems and automate data flows in minutes."
        primaryAction={{ label: "Start Free Trial", href: "/login" }}
        secondaryAction={{ label: "View Integrations", href: "/features/integrations" }}
        background="primary"
      />
    </div>
  );
}
